import React, { useState } from "react";
import { TerminalTheme } from "../types";
import { MessageCircleQuestion, X, Send, Loader2 } from "lucide-react";

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

interface Props {
  theme: TerminalTheme;
  scopedFetch: ScopedFetch;
}

// Quick-ask Gemini Flash sidebar widget (F16) — global chrome.
// For trivia, conversions, quick lookups Leah doesn't want to burn
// Claude session context on. ~$0.01/call. Open/close via FAB.

export const QuickAskWidget: React.FC<Props> = ({ theme, scopedFetch }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);

  const submit = async () => {
    if (!input.trim()) return;
    setBusy(true); setAnswer(null);
    try {
      const res = await scopedFetch("/api/gemini/chat", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", text: input }],
          system: "You are a fast helpful assistant. Answer in 1-3 sentences. No preamble.",
          caller: "quick-ask",
        }),
      });
      const data = await res.json();
      setAnswer(res.ok ? (data.text || "(no response)") : `Error: ${data.detail}`);
    } catch (e) {
      setAnswer(`Error: ${e}`);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ backgroundColor: theme.accent, color: theme.bg, boxShadow: `0 4px 12px ${theme.accent}40` }}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full flex items-center justify-center z-40 hover:scale-105 transition-transform"
        title="Quick-ask Gemini Flash (~$0.01/query)"
        aria-label="Quick ask"
      >
        <MessageCircleQuestion size={20} />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 w-80 rounded-lg z-40 shadow-2xl"
      style={{ backgroundColor: theme.bg, border: `1px solid ${theme.accent}80` }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: theme.border }}>
        <div className="text-xs font-bold flex items-center gap-2" style={{ color: theme.accent }}>
          <MessageCircleQuestion size={12} /> Quick ask (Gemini Flash)
        </div>
        <button onClick={() => { setOpen(false); setAnswer(null); setInput(""); }} aria-label="close" style={{ color: theme.systemText }}>
          <X size={14} />
        </button>
      </div>

      <div className="p-3 space-y-2 max-h-[320px] overflow-y-auto">
        {answer && (
          <div className="text-xs rounded p-2 whitespace-pre-wrap" style={{ backgroundColor: theme.bg + "80", color: theme.fg, border: `1px solid ${theme.border}` }}>
            {answer}
          </div>
        )}
        {busy && (
          <div className="text-xs flex items-center gap-2" style={{ color: theme.systemText }}>
            <Loader2 size={12} className="animate-spin" /> thinking…
          </div>
        )}
        <div className="text-[10px]" style={{ color: theme.systemText }}>
          For quick lookups — saves Margo's main session for marketing work. Don't use for drafts.
        </div>
      </div>

      <div className="flex gap-1 px-3 py-2 border-t" style={{ borderColor: theme.border }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !busy) submit(); }}
          placeholder="Quick question…"
          className="flex-1 px-2 py-1 text-xs rounded outline-none"
          style={{ backgroundColor: theme.bg + "80", color: theme.fg, border: `1px solid ${theme.border}` }}
        />
        <button onClick={submit} disabled={busy || !input.trim()} style={{ backgroundColor: theme.accent, color: theme.bg }}
                className="px-2 rounded disabled:opacity-40">
          <Send size={12} />
        </button>
      </div>
    </div>
  );
};
